import { useCallback } from 'preact/hooks';
import { h, Fragment, type ComponentChild } from 'preact';
import { resolve, renderMessage, type MessageParams } from '@goalpost/content';
import { useLocale } from './hooks/useLocale.tsx';

export type TFunction = (
  key: string,
  params?: MessageParams,
  options?: { default?: string },
) => string;

export function useT(): TFunction {
  const { locale } = useLocale();
  return useCallback(
    (key, params, options) => resolve(key, {
      locale,
      ...(params !== undefined && { params }),
      ...(options?.default !== undefined && { default: options.default }),
    }),
    [locale],
  );
}

// Like useT, but renders a message's inline MF2 markup (e.g. {#strong}…{/strong})
// as real elements, returning a VNode instead of a string. useT stays the
// default for the many string-only call sites (titles, attributes, plain text);
// reach for this only where a message's emphasis should render.
export type RichTFunction = (key: string, params?: MessageParams) => ComponentChild;

export function useRichT(): RichTFunction {
  const { locale } = useLocale();
  return useCallback(
    (key, params) => renderMessage<ComponentChild>(key, {
      locale,
      ...(params !== undefined && { params }),
      render: {
        text: (value) => value,
        element: (tag, children) => h(tag, null, ...children),
        fragment: (children) => h(Fragment, null, ...children),
      },
    }),
    [locale],
  );
}
